import { LineAngleCalculations } from '../../modules/LineAngleCalculations.js';

/** connection_type values that never count toward a node's break angle, regardless of anything else. */
export const EXCLUDED_CONNECTION_TYPES = ['reference', 'down guy', 'pushbrace', 'underground cable', 'centerline', 'right of way line'];

/** The design_category attribute and value that mark a connection as removed from the design. */
export const DESIGN_CATEGORY_ATTRIBUTE = 'design_category';
export const REMOVED_DESIGN_CATEGORY_VALUE = 'Remove';

/** The attribute that marks a connection as removed from scope. */
export const REMOVED_FROM_SCOPE_ATTRIBUTE = 'removed_from_scope';

/**
 * @typedef {object} BreakAngleConnectionCandidate
 * @property {string} id - The connection's id.
 * @property {?string} type - The connection's connection_type attribute value.
 * @property {?string} designCategory - The connection's design_category attribute value.
 * @property {?boolean} removedFromScope - The connection's removed_from_scope attribute value.
 * @property {{lat: number, lng: number}} otherLatLng - The coordinates of the connection's endpoint other than the node being evaluated.
 */

/**
 * @typedef {object} BreakAngleResolution
 * @property {'skip' | 'needsManualReview' | 'resolved'} status - `'skip'` when the node has no active connections. `'needsManualReview'`
 * when it has exactly one, since choosing the right second connection to compare it against takes human judgment. `'resolved'` when it
 * has two or more, in which case `breakAngle` and `connectionIds` are also set.
 * @property {number} [breakAngle] - Only set when `status === 'resolved'`.
 * @property {string[]} [connectionIds] - The two connection ids that produced `breakAngle`. Only set when `status === 'resolved'`.
 */

/**
 * Resolves a node's break angle from its connections. Every connection_type counts toward the break angle except
 * EXCLUDED_CONNECTION_TYPES. A connection whose design_category is "Remove" or whose removed_from_scope is true is also excluded entirely,
 * as if it weren't connected to the node.
 * @param {{lat: number, lng: number}} nodeLatLng - The coordinates of the node being evaluated (the shared midpoint of every comparison).
 * @param {BreakAngleConnectionCandidate[]} connectionCandidates - The node's connections, with type/designCategory/removedFromScope/otherLatLng already resolved.
 * @returns {BreakAngleResolution} Never throws.
 */
export function resolveNodeBreakAngle(nodeLatLng, connectionCandidates) {
  const activeConnections = connectionCandidates.filter(
    (candidate) =>
      !EXCLUDED_CONNECTION_TYPES.includes(candidate.type ?? '') &&
      candidate.designCategory !== REMOVED_DESIGN_CATEGORY_VALUE &&
      candidate.removedFromScope !== true
  );

  if (activeConnections.length === 0) return { status: 'skip' };
  if (activeConnections.length === 1) return { status: 'needsManualReview' };

  let lowest = null;
  for (const [connectionA, connectionB] of getPairwiseCombinations(activeConnections)) {
    const { breakAngle } = LineAngleCalculations(connectionA.otherLatLng, nodeLatLng, connectionB.otherLatLng);
    const breakAngleNumber = Number(breakAngle);
    if (lowest == null || breakAngleNumber < lowest.breakAngle) {
      lowest = { breakAngle: breakAngleNumber, connectionIds: [connectionA.id, connectionB.id] };
    }
  }
  return { status: 'resolved', ...lowest };
}

/**
 * @param {BreakAngleConnectionCandidate[]} items
 * @returns {[BreakAngleConnectionCandidate, BreakAngleConnectionCandidate][]}
 */
function getPairwiseCombinations(items) {
  const pairs = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      pairs.push([items[i], items[j]]);
    }
  }
  return pairs;
}
